import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-public";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CountryCombobox } from "@/components/CountryCombobox";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { CustomerWhatsAppFields, whatsAppPhoneError, type CustomerChannel } from "@/components/CustomerWhatsApp";
import { useWhatsAppEnabled } from "@/hooks/useWhatsAppEnabled";
type CustomerResponse = components["schemas"]["CustomerResponse"];

export interface EditCustomerDialogProps {
  customer: CustomerResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

export function EditCustomerDialog({
  customer,
  open,
  onOpenChange,
  onUpdated,
}: EditCustomerDialogProps) {
  const api = useDolfinClient();
  const whatsAppEnabled = useWhatsAppEnabled();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [electronicAddressScheme, setElectronicAddressScheme] = useState("");
  const [electronicAddress, setElectronicAddress] = useState("");
  // Always sent, because PUT replaces every field: without the module these carry the saved values through.
  const [whatsAppPhone, setWhatsAppPhone] = useState("");
  const [preferredChannel, setPreferredChannel] = useState<CustomerChannel>("Email");
  const [whatsAppError, setWhatsAppError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (customer) {
      setName(customer.name ?? "");
      setEmail(customer.email ?? "");
      setPhone(customer.phone ?? "");
      setTaxNumber(customer.taxNumber ?? "");
      setAddressLine1(customer.addressLine1 ?? "");
      setAddressLine2(customer.addressLine2 ?? "");
      setCity(customer.city ?? "");
      setState(customer.state ?? "");
      setPostalCode(customer.postalCode ?? "");
      setCountry(customer.country ?? "");
      setElectronicAddressScheme(customer.electronicAddressScheme ?? "");
      setElectronicAddress(customer.electronicAddress ?? "");
      setWhatsAppPhone(customer.whatsAppPhone ?? "");
      setPreferredChannel(customer.preferredChannel ?? "Email");
      setWhatsAppError(null);
      setError(null);
    }
  }, [customer]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setSubmitting(false);
      setError(null);
      setWhatsAppError(null);
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customer) return;
    setError(null);
    setWhatsAppError(null);

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }

    // The country picks the e-invoice route (SDI in Italy, Peppol elsewhere); the API refuses an address without one.
    if (electronicAddressScheme && !country) {
      setError("Choose the customer's country: it decides how their e-invoices are delivered.");
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.PUT("/v1/customers/{id}", {
      params: { path: { id: customer.id! } },
      body: {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        taxNumber: taxNumber.trim() || null,
        addressLine1: addressLine1.trim() || null,
        addressLine2: addressLine2.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        postalCode: postalCode.trim() || null,
        country: country.trim() || null,
        electronicAddressScheme: (electronicAddressScheme || null) as
          components["schemas"]["EInvoiceAddressScheme"] | null,
        electronicAddress: electronicAddress.trim() || null,
        // "" removes the number; null would leave it unchanged.
        whatsAppPhone: whatsAppPhone.trim(),
        preferredChannel,
      },
    });

    if (apiError) {
      const message = apiErrorMessage(apiError, "Failed to update customer.");
      // A WhatsApp number error goes on its field when the field is showing; every failure is also toasted.
      const fieldError = whatsAppEnabled ? whatsAppPhoneError(apiError) : null;
      if (fieldError) setWhatsAppError(fieldError);
      else setError(message);
      toast.error(fieldError ?? message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onUpdated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>Update customer details.</DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-customer-name">Name</Label>
              <Input
                id="edit-customer-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-customer-email">Email</Label>
                <Input
                  id="edit-customer-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-customer-phone">Phone</Label>
                <Input
                  id="edit-customer-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-customer-tax">Tax Number</Label>
              <Input
                id="edit-customer-tax"
                value={taxNumber}
                onChange={(e) => setTaxNumber(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
                Address
              </Label>
              <Input
                placeholder="Address line 1"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
              />
              <Input
                placeholder="Address line 2"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
                <Input
                  placeholder="State / Region"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Postal code"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                />
                <CountryCombobox
                  value={country}
                  onChange={setCountry}
                  placeholder={electronicAddressScheme ? "Country (required)" : "Country"}
                  inDialog
                />
              </div>
            </div>

            {/* A routing address, unrelated to the tax number: where a document goes, not who the party is. */}
            <div className="space-y-2">
              <Label>E-invoicing address</Label>
              <div className="grid grid-cols-2 gap-4">
                <select
                  className="border-input bg-background h-9 rounded-md border px-3 text-sm"
                  value={electronicAddressScheme}
                  onChange={(e) => {
                    const scheme = e.target.value;
                    setElectronicAddressScheme(scheme);
                    // A codice destinatario or PEC is only for a customer in Italy.
                    if (scheme.startsWith("ItalianSdi") && !country) setCountry("IT");
                  }}
                >
                  <option value="">Not set up</option>
                  <option value="Peppol">Peppol participant id</option>
                  <option value="ItalianSdiCode">Italy: codice destinatario</option>
                  <option value="ItalianSdiPec">Italy: PEC address</option>
                </select>
                <Input
                  placeholder={
                    electronicAddressScheme === "ItalianSdiCode"
                      ? "M5UXCR5"
                      : electronicAddressScheme === "ItalianSdiPec"
                        ? "customer@pec.it"
                        : "0208:0455341259"
                  }
                  value={electronicAddress}
                  onChange={(e) => setElectronicAddress(e.target.value)}
                  disabled={!electronicAddressScheme}
                />
              </div>
            </div>

            {whatsAppEnabled && (
              <CustomerWhatsAppFields
                idPrefix="edit-customer"
                phone={whatsAppPhone}
                onPhoneChange={(v) => {
                  setWhatsAppPhone(v);
                  setWhatsAppError(null);
                }}
                channel={preferredChannel}
                onChannelChange={setPreferredChannel}
                error={whatsAppError}
                hasConsent={!!customer?.hasWhatsAppConsent}
              />
            )}

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
